import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { AuthenticationService } from 'src/service/authentication.service';


@Injectable({ providedIn: 'root' })
export class InterceptorToken implements HttpInterceptor {

  private static isRefreshing = false;
  private static refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private authenticationService: AuthenticationService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (this.shouldIntercept(request.url)) {
      // Pega a próxima token de acesso
      const token: any = InterceptorToken.getAccessToken();

      if (token && token.access_token) {
        // Insere o cabeçalho de autorização na requisição HTTP
        request = this.setTokenHeader(request, token.authenticationType, token.access_token);
      }
    }

    return next.handle(request).pipe(
      catchError(error => {

        if (this.isAuthError(request, error)) {
          // Tenta fazer o refresh do token
          return this.handle401Error(request, next);
        }

        // Falha normal de autenticação 
        return throwError(() => error);
      })
    );
  }

  private shouldIntercept(url: string): boolean {
    return url.includes('/api/') &&
      !url.includes('/connect/token') &&
      !url.includes('/recoveryPassword') &&
      !url.includes('/changePasswordWithToken') &&
      !url.includes('/login');
  }

  private isAuthError(request: HttpRequest<any>, error: any): boolean {
    // Verifica se o erro é 401 (não autorizado)
    return request.headers.get('Authorization')?.startsWith('Bearer') &&
      error.error?.code === "FE005" &&
      error instanceof HttpErrorResponse &&
      error.status === 401;
  }

  /**
   * Trata o erro 401 - Não autorizado fazendo o refresh da token
   */
  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!InterceptorToken.isRefreshing) {
      InterceptorToken.isRefreshing = true;
      InterceptorToken.refreshTokenSubject.next(null);

      const refresh_token = InterceptorToken.getRefreshToken();

      if (refresh_token !== null) {

        const urlBase = request.url.split('/api/')[0];

        // Faz o refresh do token expirado
        return this.authenticationService.refreshToken(refresh_token.toString(), urlBase)
          .pipe(
            switchMap((token: any) => {
              InterceptorToken.isRefreshing = false;

              // Salva o novo token de acesso e o refresh token na sessionStorage
              this.saveToken(token);

              InterceptorToken.refreshTokenSubject.next(token.access_token);

              // Faz a requisição novamente com o token atualizado
              return next.handle(this.setTokenHeader(request, token.token_type, token.access_token));
            }),
            catchError((error) => {
              if(InterceptorToken.isRefreshing){
              InterceptorToken.isRefreshing = false;
              return throwError(() => error);
              }
            })
          );

      } else {
        return throwError(() => 'Token is null');
      }
    } else {
      return InterceptorToken.refreshTokenSubject.pipe(
        filter(access_token => access_token != null),
        take(1),
        switchMap(access_token => next.handle(this.setTokenHeader(request, 'Bearer', access_token)))
      );
    }
  }


  /**
   * Salva o novo token de acesso e o refresh token na sessionStorage
   */
  saveToken(token: any) {

    // Atualiza contexto da MDI
    if (sessionStorage.getItem('ERPTOKEN')) {
      const erpToken = JSON.parse(sessionStorage.getItem('ERPTOKEN'));
      erpToken.access_token = token.access_token;
      erpToken.refresh_token = token.refresh_token;
      sessionStorage.setItem('ERPTOKEN', JSON.stringify(erpToken));
    }

    // Atualiza contexto do Portal Top Web - Azure SAML
    if (sessionStorage.getItem('tokenSaml')) {
      const tokenSaml = JSON.parse(sessionStorage.getItem('tokenSaml'));
      tokenSaml.accessToken = token.access_token;
      tokenSaml.refreshToken = token.refresh_token;
      sessionStorage.setItem('tokenSaml', JSON.stringify(tokenSaml));

      // Situação quando o portal top web abre outro portal em uma aba separada que não enxerga a mesma variável do sessionStorage
      localStorage.setItem('tokenSaml', JSON.stringify(tokenSaml));
    }

    // Atualiza contexto da Portal Tob Web - Bearer
    if (sessionStorage.getItem('tokenBearer')) {
      const tokenBearer = JSON.parse(sessionStorage.getItem('tokenBearer'));
      tokenBearer.access_token = token.access_token;
      tokenBearer.refresh_token = token.refresh_token;
      sessionStorage.setItem('tokenBearer', JSON.stringify(tokenBearer));
    }
  }

  /**
   * Seta o token de acesso no cabeçalho da requisição
   */
  public setTokenHeader(request: HttpRequest<any>, authenticationType: any, access_token: any) {
    return request.clone({
      setHeaders: {
        Authorization: `${authenticationType} ${access_token}`
      }
    });
  }


  /**
   * Retorna o token de refresh
   */
  public static getRefreshToken(): string | null {

    // É via MDI ?
    const erpToken = sessionStorage.getItem('ERPTOKEN');
    if (erpToken) {
      return JSON.parse(erpToken).refresh_token;
    }

    // É via Portal Top Web - Azure SAML ?
    const tokenSaml = sessionStorage.getItem('tokenSaml');
    if (tokenSaml !== null) {
      return JSON.parse(tokenSaml).refreshToken;
    }

    // É via Portal Top Web - Bearer ?
    const tokenBearer = sessionStorage.getItem('tokenBearer');
    if (tokenBearer !== null) {
      return JSON.parse(tokenBearer).refresh_token;
    }

    return undefined;
  }

  /**
     * Pega a token de acesso
     */
  public static getAccessToken(): any {
    const result: any = {};

    // É via MDI ?
    const erpToken = sessionStorage.getItem('ERPTOKEN');
    if (erpToken) {
      result.authenticationType = 'Bearer';
      result.access_token = JSON.parse(erpToken).access_token;
      return result;
    }

    // É via portal top web - Azure SAML ?
    const tokenSaml = sessionStorage.getItem('tokenSaml');
    if (tokenSaml) {
      result.authenticationType = 'Bearer';
      result.access_token = JSON.parse(tokenSaml).accessToken;
      return result;
    }

    // É via portal top web - Bearer ?
    const tokenBearer = sessionStorage.getItem('tokenBearer');
    if (tokenBearer) {
      result.authenticationType = 'Bearer';
      result.access_token = JSON.parse(tokenBearer).access_token;
      return result;
    }

    // Portal Top Web - sem Azure SAML
    const token = sessionStorage.getItem('token');
    if (token) {
      result.authenticationType = 'Basic';
      result.access_token = token;
      return result;
    }
    return result;
  }

  
}
